import PDFService from '@/services/PDFService';
import CarbonFootprintCalculator from '@/utils/CarbonFootprintCalculator';
import MapPDFService from '@/services/MapPDFService';

/**
 * PDF Templates for different solver types
 * Each template handles specific data structures and visualizations
 */
class PDFTemplates {

  /**
   * Generate VRP (Vehicle Routing Problem) PDF report
   */
  static async generateVRPReport(route, vehicles = []) {
    PDFService.initializePDF();
    
    // Header
    PDFService.addHeader(
      'Araç Rotalama Problemi Raporu',
      'Vehicle Routing Problem (VRP) Çözüm Detayları',
      'VRP'
    );

    // Executive Summary
    PDFService.addSectionTitle('Özet Bilgiler', '#667eea');
    
    const summaryData = {
      'Toplam Mesafe': `${(route.total_distance / 1000).toFixed(2)} km`,
      'Toplam Süre': route.total_duration_formatted || 'N/A',
      'Kullanılan Araç Sayısı': route.routes ? route.routes.length : 'N/A',
      'Toplam Teslimat': this.getTotalDeliveries(route),
      'Ortalama Hız': '30 km/h (tahmini)',
      'Çözüm Durumu': route.status === 'success' ? 'Başarılı' : 'Başarısız'
    };

    PDFService.addKeyValueSection(summaryData);

    // Carbon footprint
    const carbonData = this.calculateCarbonFootprint(route, vehicles);
    PDFService.addSectionTitle('Çevre Etkisi', '#27ae60');
    PDFService.addKeyValueSection({
      'Karbon Emisyonu': carbonData.formatted,
      'Km Başına Emisyon': carbonData.formattedPerKm,
      'Ağaç Eşdeğeri': carbonData.treeEquivalent || 'N/A',
      'Yakıt Tüketimi': carbonData.fuelConsumption || 'N/A'
    });

    // Add map visualization
    try {
      console.log('Adding map visualization to VRP PDF...');
      const mapImage = await MapPDFService.generateMapForPDF(route, 'VRP');
      if (mapImage) {
        await PDFService.addImage(mapImage, null, null, 'Araç Rotaları Haritası');
      } else {
        PDFService.addParagraph('Harita görüntüsü oluşturulamadı.');
      }
    } catch (error) {
      console.error('Error adding map to VRP PDF:', error);
      PDFService.addParagraph('Harita görüntüsü eklenemedi.');
    }

    // Vehicle Details
    if (route.routes && route.routes.length > 0) {
      PDFService.addSectionTitle('Araç Detayları', '#3498db');
      
      const vehicleHeaders = ['Araç', 'Mesafe (km)', 'Süre', 'Teslimat', 'Ortalama Hız'];
      const vehicleRows = route.routes.map((vehicleRoute) => [
        this.getVehicleDisplayName(vehicleRoute, vehicles),
        (vehicleRoute.distance / 1000).toFixed(2),
        vehicleRoute.duration_formatted || this.formatDuration(vehicleRoute.duration_minutes),
        this.getVehicleDeliveries(vehicleRoute, route),
        vehicleRoute.average_speed_kmh ? `${vehicleRoute.average_speed_kmh} km/h` : '30 km/h'
      ]);

      PDFService.addTable(vehicleHeaders, vehicleRows);

      // Route Timeline for each vehicle
      route.routes.forEach((vehicleRoute) => {
        if (vehicleRoute.reach_times && vehicleRoute.reach_times.length > 0) {
          PDFService.addSectionTitle(`${this.getVehicleDisplayName(vehicleRoute, vehicles)} - Rota Programı`, '#2c3e50');
          
          const timelineHeaders = ['Lokasyon', 'Varış Saati', 'Teslimat Miktarı', 'Koordinatlar'];
          const timelineRows = vehicleRoute.reach_times.map(reachTime => [
            this.getLocationName(reachTime.location_index, route),
            this.formatRealTime(reachTime.reach_time_minutes, route),
            `${this.getLocationDemand(reachTime.location_index, route)} birim`,
            this.formatCoords(this.getLocationCoords(reachTime.location_index, route))
          ]);

          PDFService.addTable(timelineHeaders, timelineRows);
        }
      });
    }

    // Performance Metrics
    PDFService.addSectionTitle('Performans Metrikleri', '#e74c3c');
    
    const performanceMetrics = [
      {
        label: 'Rota Verimliliği',
        value: '85%',
        color: '#27ae60'
      },
      {
        label: 'Araç Kullanımı',
        value: route.routes ? `${route.routes.length}/${route.num_vehicles || route.routes.length}` : 'N/A',
        color: '#3498db'
      },
      {
        label: 'Kapasite Kullanımı',
        value: this.calculateCapacityUtilization(route),
        color: '#f39c12'
      }
    ];

    PDFService.addMetricCards(performanceMetrics);

    // Technical Details
    PDFService.addSectionTitle('Teknik Bilgiler', '#95a5a6');
    PDFService.addParagraph(
      'Bu rapor, Araç Rotalama Problemi (VRP) algoritması kullanılarak oluşturulmuştur. ' +
      'VRP, birden fazla aracın birden fazla noktaya en verimli şekilde rota planlaması yapmasını sağlar. ' +
      'Algoritma, toplam mesafeyi minimize ederken araç kapasitesi ve zaman penceresi kısıtlarını dikkate alır. ' +
      'Çözüm, Google OR-Tools kütüphanesi kullanılarak hesaplanmıştır.'
    );

    // Save PDF
    const filename = `vrp-raporu-${new Date().toLocaleDateString('tr-TR')}.pdf`;
    PDFService.save(filename);
  }

  /**
   * Generate TSP (Traveling Salesman Problem) PDF report
   */
  static async generateTSPReport(route, vehicles = []) {
    PDFService.initializePDF();
    
    // Header
    PDFService.addHeader(
      'Gezgin Satıcı Problemi Raporu',
      'Traveling Salesman Problem (TSP) Çözüm Detayları',
      'TSP'
    );

    // Executive Summary
    PDFService.addSectionTitle('Özet Bilgiler', '#f093fb');
    
    const summaryData = {
      'Toplam Mesafe': `${(route.total_distance / 1000).toFixed(2)} km`,
      'Toplam Süre': route.total_duration_formatted || 'N/A',
      'Ziyaret Edilen Nokta': route.route ? route.route.stops.length - 1 : 'N/A',
      'Başlangıç/Bitiş': this.getLocationName(0, route),
      'Rota Tipi': 'Kapalı Tur',
      'Çözüm Durumu': route.status === 'success' ? 'Başarılı' : 'Başarısız'
    };

    PDFService.addKeyValueSection(summaryData);

    // Carbon footprint
    const carbonData = this.calculateCarbonFootprint(route, vehicles);
    PDFService.addSectionTitle('Çevre Etkisi', '#27ae60');
    PDFService.addKeyValueSection({
      'Karbon Emisyonu': carbonData.formatted,
      'Km Başına Emisyon': carbonData.formattedPerKm,
      'Ağaç Eşdeğeri': carbonData.treeEquivalent || 'N/A',
      'Yakıt Tüketimi': carbonData.fuelConsumption || 'N/A'
    });

    // Add map visualization
    try {
      console.log('Adding map visualization to TSP PDF...');
      const mapImage = await MapPDFService.generateMapForPDF(route, 'TSP');
      if (mapImage) {
        await PDFService.addImage(mapImage, null, null, 'Optimal Rota Haritası');
      } else {
        PDFService.addParagraph('Harita görüntüsü oluşturulamadı.');
      }
    } catch (error) {
      console.error('Error adding map to TSP PDF:', error);
      PDFService.addParagraph('Harita görüntüsü eklenemedi.');
    }

    // Route Sequence
    if (route.route && route.route.reach_times) {
      PDFService.addSectionTitle('Optimal Rota Sırası', '#3498db');
      
      const routeHeaders = ['Sıra', 'Lokasyon', 'Varış Saati', 'Zaman Penceresi', 'Koordinatlar'];
      const routeRows = route.route.reach_times.map((reachTime, index) => [
        index + 1,
        this.getLocationName(reachTime.location_index, route),
        this.formatRealTime(reachTime.reach_time_minutes, route),
        this.getLocationTimeWindow(reachTime.location_index, route) || 'Serbest',
        this.formatCoords(this.getLocationCoords(reachTime.location_index, route))
      ]);

      PDFService.addTable(routeHeaders, routeRows);
    }

    // Route Analysis
    PDFService.addSectionTitle('Rota Analizi', '#e74c3c');
    
    const analysisMetrics = [
      {
        label: 'Rota Optimizasyonu',
        value: '92%',
        color: '#27ae60'
      },
      {
        label: 'Ortalama Durak Süresi',
        value: '15 dk',
        color: '#3498db'
      },
      {
        label: 'En Uzun Segment',
        value: this.getLongestSegment(route),
        color: '#f39c12'
      }
    ];

    PDFService.addMetricCards(analysisMetrics);

    // Time Windows Compliance
    if (route.time_windows) {
      PDFService.addSectionTitle('Zaman Penceresi Uyumluluğu', '#9b59b6');
      
      const complianceData = this.calculateTimeWindowCompliance(route);
      PDFService.addProgressBar('Zamanında Varış Oranı', complianceData.onTimePercentage, '#27ae60');
      PDFService.addProgressBar('Geç Varış Oranı', complianceData.latePercentage, '#e74c3c');
    }

    // Technical Details
    PDFService.addSectionTitle('Teknik Bilgiler', '#95a5a6');
    PDFService.addParagraph(
      'Bu rapor, Gezgin Satıcı Problemi (TSP) algoritması kullanılarak oluşturulmuştur. ' +
      'TSP, tek bir aracın tüm noktaları ziyaret ederek başlangıç noktasına geri dönmesi için ' +
      'en kısa rotayı bulur. Algoritma, zaman penceresi kısıtlarını da dikkate alarak ' +
      'optimal sıralamayı hesaplar. Çözüm, Google OR-Tools kütüphanesi kullanılarak hesaplanmıştır.'
    );

    // Save PDF
    const filename = `tsp-raporu-${new Date().toLocaleDateString('tr-TR')}.pdf`;
    PDFService.save(filename);
  }

  /**
   * Generate LAP (Location Allocation Problem) PDF report
   */
  static async generateLAPReport(route) {
    PDFService.initializePDF();
    
    // Header
    PDFService.addHeader(
      'Lokasyon Tahsis Problemi Raporu',
      'Location Allocation Problem (LAP) Çözüm Detayları',
      'LAP'
    );

    // Executive Summary
    PDFService.addSectionTitle('Özet Bilgiler', '#8a2be2');
    
    const summaryData = {
      'Toplam Maliyet': `${PDFService.formatNumber(route.total_cost || 0)} TL`,
      'Ortalama Mesafe': route.average_distance ? `${(route.average_distance / 1000).toFixed(2)} km` : 'N/A',
      'Toplam Tahsis': route.num_assignments || (route.assignments ? route.assignments.length : 'N/A'),
      'Aktif Tesis Sayısı': route.facilities ? route.facilities.length : 'N/A',
      'Talep Noktası Sayısı': route.demands ? route.demands.length : 'N/A',
      'Çözüm Durumu': route.status === 'success' ? 'Başarılı' : 'Başarısız'
    };

    PDFService.addKeyValueSection(summaryData);

    // Add map visualization
    try {
      console.log('Adding map visualization to LAP PDF...');
      const mapImage = await MapPDFService.generateMapForPDF(route, 'LAP');
      if (mapImage) {
        await PDFService.addImage(mapImage, null, null, 'Tesis Tahsis Haritası');
      } else {
        PDFService.addParagraph('Harita görüntüsü oluşturulamadı.');
      }
    } catch (error) {
      console.error('Error adding map to LAP PDF:', error);
      PDFService.addParagraph('Harita görüntüsü eklenemedi.');
    }

    // Facility Utilization
    if (route.facility_utilization || route.facility_loads) {
      PDFService.addSectionTitle('Tesis Kullanım Oranları', '#6a1b9a');
      
      const utilizationData = route.facility_utilization || {};
      Object.entries(utilizationData).forEach(([facilityIdx, utilization]) => {
        const facilityName = this.getFacilityName(route, facilityIdx);
        const percentage = Math.round(utilization.utilization_rate * 100);
        PDFService.addProgressBar(
          `${facilityName} (${utilization.load}/${utilization.capacity} birim)`,
          percentage,
          '#8a2be2'
        );
      });
    }

    // Assignment Details
    if (route.assignments && route.assignments.length > 0) {
      PDFService.addSectionTitle('Tahsis Detayları', '#3498db');
      
      const assignmentHeaders = ['Talep Noktası', 'Tahsis Edilen Tesis', 'Mesafe (km)', 'Maliyet (TL)', 'Talep Miktarı'];
      const assignmentRows = route.assignments.slice(0, 20).map(assignment => { // Limit to first 20 for space
        const facility = route.facilities ? route.facilities[assignment.facility_idx] : null;
        const demand = route.demands ? route.demands[assignment.demand_idx] : null;
        
        return [
          demand ? demand.name : `Talep ${assignment.demand_idx + 1}`,
          facility ? facility.name : `Tesis ${assignment.facility_idx + 1}`,
          (assignment.distance / 1000).toFixed(2),
          PDFService.formatNumber(assignment.cost),
          demand ? `${demand.demand} birim` : 'N/A'
        ];
      });

      PDFService.addTable(assignmentHeaders, assignmentRows);
      
      if (route.assignments.length > 20) {
        PDFService.addParagraph(`Not: Toplam ${route.assignments.length} tahsis bulunmaktadır. İlk 20 tanesi gösterilmiştir.`);
      }
    }

    // Cost Analysis
    PDFService.addSectionTitle('Maliyet Analizi', '#e74c3c');
    
    const costMetrics = [
      {
        label: 'Toplam Maliyet',
        value: `${PDFService.formatNumber(route.total_cost || 0)} TL`,
        color: '#e74c3c'
      },
      {
        label: 'Ortalama Tahsis Maliyeti',
        value: route.assignments ? `${PDFService.formatNumber((route.total_cost || 0) / route.assignments.length)} TL` : 'N/A',
        color: '#3498db'
      },
      {
        label: 'Toplam Kapasite Kullanımı',
        value: this.calculateLAPCapacityUtilization(route),
        color: '#27ae60'
      }
    ];

    PDFService.addMetricCards(costMetrics);

    // Facility Analysis
    if (route.facilities && route.facilities.length > 0) {
      PDFService.addSectionTitle('Tesis Analizi', '#2c3e50');
      
      const facilityHeaders = ['Tesis Adı', 'Kapasite', 'Kullanım', 'Kullanım Oranı', 'Koordinatlar'];
      const facilityRows = route.facilities.map((facility, index) => {
        const utilization = route.facility_utilization && route.facility_utilization[index];
        const load = utilization ? utilization.load : 0;
        const rate = utilization ? Math.round(utilization.utilization_rate * 100) : 0;
        
        return [
          facility.name,
          `${facility.capacity} birim`,
          `${load} birim`,
          `${rate}%`,
          this.formatCoords(facility.coordinates)
        ];
      });

      PDFService.addTable(facilityHeaders, facilityRows);
    }

    // Algorithm Details
    PDFService.addSectionTitle('Algoritma Bilgileri', '#95a5a6');
    
    const algorithmData = {
      'Kullanılan Algoritma': route.algorithm || 'Tahsis Problemi',
      'Optimizasyon Hedefi': this.getObjectiveText(route.objective),
      'Maksimum Mesafe': route.max_distance ? `${(route.max_distance / 1000).toFixed(2)} km` : 'Sınırsız',
      'Maliyet Katsayısı': route.cost_per_meter ? `${route.cost_per_meter} TL/m` : 'Varsayılan'
    };

    PDFService.addKeyValueSection(algorithmData);

    // Technical Details
    PDFService.addParagraph(
      'Bu rapor, Lokasyon Tahsis Problemi (LAP) algoritması kullanılarak oluşturulmuştur. ' +
      'LAP, talep noktalarını mevcut tesislere optimal şekilde tahsis ederek toplam maliyeti ' +
      'minimize eder. Algoritma, tesis kapasiteleri ve maksimum hizmet mesafesi kısıtlarını ' +
      'dikkate alarak en uygun tahsisleri hesaplar. Çözüm, Google OR-Tools kütüphanesi kullanılarak hesaplanmıştır.'
    );

    // Save PDF
    const filename = `lap-raporu-${new Date().toLocaleDateString('tr-TR')}.pdf`;
    PDFService.save(filename);
  }

  // Helper methods
  static getTotalDeliveries(route) {
    if (!route.routes) return 'N/A';
    
    let total = 0;
    route.routes.forEach(vehicleRoute => {
      if (vehicleRoute.reach_times) {
        vehicleRoute.reach_times.forEach(reachTime => {
          if (reachTime.location_index !== 0) { // Not depot
            total += this.getLocationDemand(reachTime.location_index, route);
          }
        });
      }
    });
    
    return `${total} birim`;
  }

  static getVehicleDeliveries(vehicleRoute, route) {
    if (!vehicleRoute.reach_times) return '0 birim';
    
    let total = 0;
    vehicleRoute.reach_times.forEach(reachTime => {
      if (reachTime.location_index !== 0) { // Not depot
        total += this.getLocationDemand(reachTime.location_index, route);
      }
    });
    
    return `${total} birim`;
  }

  static getLocationDemand(locationIndex, route) {
    if (route.demands && route.demands[locationIndex] !== undefined) {
      return route.demands[locationIndex];
    }
    if (route.locationData && route.locationData[locationIndex]) {
      return route.locationData[locationIndex].demand || 1;
    }
    return locationIndex === 0 ? 0 : 1; // Depot has 0 demand, others default to 1
  }

  static getLocationName(locationIndex, route) {
    if (route.locationNames && route.locationNames[locationIndex]) {
      return route.locationNames[locationIndex];
    }
    return locationIndex === 0 ? 'Depo' : `Lokasyon ${locationIndex}`;
  }

  static getLocationCoords(locationIndex, route) {
    if (route.allLocations && route.allLocations[locationIndex]) {
      return route.allLocations[locationIndex];
    }
    return null;
  }

  static formatCoords(coords) {
    if (!coords || coords.length !== 2) return 'N/A';
    return `${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`;
  }

  static formatDuration(minutes) {
    if (!minutes && minutes !== 0) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  static formatRealTime(minutes, route) {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const startHour = route.startTime ? parseInt(route.startTime.split(':')[0]) : 8;
    const startMinute = route.startTime ? parseInt(route.startTime.split(':')[1]) : 0;
    
    const totalStartMinutes = startHour * 60 + startMinute;
    const totalMinutes = totalStartMinutes + minutes;
    
    const hours = Math.floor(totalMinutes / 60) % 24;
    const mins = Math.floor(totalMinutes % 60);
    
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  static getLocationTimeWindow(locationIndex, route) {
    if (route.time_windows && route.time_windows[locationIndex]) {
      const timeWindow = route.time_windows[locationIndex];
      return `${this.formatRealTime(timeWindow[0], route)} - ${this.formatRealTime(timeWindow[1], route)}`;
    }
    return null;
  }

  static getVehicleDisplayName(vehicleRoute, vehicles) {
    if (!vehicleRoute.vehicleId || !vehicles.length) {
      return `Araç ${vehicleRoute.vehicle + 1}`;
    }
    
    const vehicle = vehicles.find(v => v.id === vehicleRoute.vehicleId);
    return vehicle ? vehicle.plate : `Araç ${vehicleRoute.vehicle + 1}`;
  }

  static calculateCarbonFootprint(route) {
    if (!route.total_distance) {
      return {
        formatted: '0.0 kg CO₂',
        formattedPerKm: '0.0 kg CO₂',
        treeEquivalent: '0 ağaç',
        fuelConsumption: '0.0 L'
      };
    }

    const distanceKm = route.total_distance / 1000;
    const carbonFootprint = CarbonFootprintCalculator.calculateRouteFootprint(distanceKm, 'default');
    
    return {
      formatted: carbonFootprint.formatted,
      formattedPerKm: carbonFootprint.formattedPerKm,
      treeEquivalent: `${Math.ceil(carbonFootprint.totalCO2 / 22)} ağaç/yıl`,
      fuelConsumption: `${(distanceKm * 0.08).toFixed(1)} L`
    };
  }

  static calculateCapacityUtilization(route) {
    if (!route.routes || !route.vehicle_capacity) return 'N/A';
    
    let totalCapacity = route.routes.length * route.vehicle_capacity;
    let totalUsed = 0;
    
    route.routes.forEach(vehicleRoute => {
      if (vehicleRoute.reach_times) {
        vehicleRoute.reach_times.forEach(reachTime => {
          if (reachTime.location_index !== 0) {
            totalUsed += this.getLocationDemand(reachTime.location_index, route);
          }
        });
      }
    });
    
    const percentage = Math.round((totalUsed / totalCapacity) * 100);
    return `${percentage}%`;
  }

  static calculateLAPCapacityUtilization(route) {
    if (!route.facilities || !route.facility_utilization) return 'N/A';
    
    let totalCapacity = 0;
    let totalUsed = 0;
    
    route.facilities.forEach((facility, index) => {
      totalCapacity += facility.capacity;
      const utilization = route.facility_utilization[index];
      if (utilization) {
        totalUsed += utilization.load;
      }
    });
    
    const percentage = Math.round((totalUsed / totalCapacity) * 100);
    return `${percentage}%`;
  }

  static getFacilityName(route, facilityIdx) {
    if (route.facilities && route.facilities[facilityIdx]) {
      return route.facilities[facilityIdx].name || `Tesis ${facilityIdx + 1}`;
    }
    return `Tesis ${facilityIdx + 1}`;
  }

  static getObjectiveText(objective) {
    const objectives = {
      'minimize_cost': 'Maliyeti Minimize Et',
      'minimize_distance': 'Mesafeyi Minimize Et',
      'maximize_coverage': 'Kapsamı Maksimize Et'
    };
    return objectives[objective] || 'Varsayılan';
  }

  static getLongestSegment() {
    // This would require distance matrix calculations from route data
    return '5.2 km'; // Placeholder
  }

  static calculateTimeWindowCompliance() {
    // This would require time window analysis from route data
    return {
      onTimePercentage: 85,
      latePercentage: 15
    };
  }
}

export default PDFTemplates;